import { useState, useEffect, useRef } from 'react';
import { supabase } from '../../../lib/supabase';
import { generateForm4811PDF, downloadPDF, type Form4811PDFData } from '../../../lib/generateForm4811PDF';

// ── PDF Preview Modal ──────────────────────────────────────────────────────
interface PreviewModalProps {
  onClose: () => void;
  onGenerate: () => Promise<Uint8Array>;
  formTitle: string;
}

function PDFPreviewModal({ onClose, onGenerate, formTitle }: PreviewModalProps) {
  const [loading, setLoading] = useState(true);
  const [previewError, setPreviewError] = useState('');
  const [pdfUrl, setPdfUrl] = useState('');
  const urlRef = useRef('');

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const bytes = await onGenerate();
        if (cancelled) return;
        const blob = new Blob([bytes], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        urlRef.current = url;
        setPdfUrl(url);
      } catch {
        if (!cancelled) setPreviewError('Could not generate preview. Please try again.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div
        className="bg-[#0d1520] border-2 border-[#FDB913]/40 rounded-2xl w-full max-w-3xl flex flex-col overflow-hidden"
        style={{ maxHeight: '90vh' }}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center bg-[#FDB913]/20 rounded-lg border border-[#FDB913]/40">
              <i className="ri-file-pdf-line text-[#FDB913] text-lg"></i>
            </div>
            <div>
              <p className="text-white font-black text-sm">PDF Preview — {formTitle}</p>
              <p className="text-white/40 text-xs">Review before submitting</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-white/40 hover:text-white hover:bg-white/10 rounded-lg transition-all cursor-pointer"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>
        <div className="flex-1 overflow-hidden min-h-0 bg-gray-200">
          {loading && (
            <div className="flex flex-col items-center justify-center h-64 gap-3">
              <i className="ri-loader-4-line animate-spin text-[#FDB913] text-3xl"></i>
              <p className="text-gray-600 text-sm font-bold">Generating preview...</p>
            </div>
          )}
          {previewError && (
            <div className="flex flex-col items-center justify-center h-64 gap-3">
              <i className="ri-error-warning-line text-red-500 text-3xl"></i>
              <p className="text-red-600 text-sm font-bold">{previewError}</p>
            </div>
          )}
          {pdfUrl && !loading && (
            <iframe
              src={pdfUrl}
              className="w-full h-full"
              style={{ minHeight: '60vh' }}
              title={`${formTitle} Preview`}
            />
          )}
        </div>
        <div className="flex items-center justify-between px-5 py-3 border-t border-white/10 flex-shrink-0 bg-[#0d1520]">
          <p className="text-white/30 text-xs">
            <i className="ri-information-line mr-1"></i>
            Preview only. Submit the form to generate the final signed PDF.
          </p>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white font-bold text-sm rounded-lg transition-all cursor-pointer whitespace-nowrap"
          >
            Close Preview
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Tooltip ────────────────────────────────────────────────────────────────
function ImporterNumberTooltip({ visible }: { visible: boolean }) {
  if (!visible) return null;
  return (
    <div className="absolute left-0 top-full mt-1.5 z-30 w-72 bg-[#0d1520] border border-[#FDB913]/40 rounded-xl p-3 shadow-2xl">
      <div className="flex items-start gap-2">
        <i className="ri-file-search-line text-[#FDB913] text-sm flex-shrink-0 mt-0.5"></i>
        <div>
          <p className="text-[#FDB913] font-black text-xs mb-1">Where to find your Importer Number</p>
          <ul className="text-white/60 text-[10px] leading-relaxed space-y-1">
            <li><strong className="text-white">CBP Form 7501</strong> — Entry Summary, Box 26 &ldquo;Importer No.&rdquo;</li>
            <li><strong className="text-white">ACE Portal</strong> — Under your importer profile</li>
            <li><strong className="text-white">Format:</strong> EIN (XX-XXXXXXX) or SSN (XXX-XX-XXXX) or CBP-assigned number</li>
          </ul>
          <p className="text-white/30 text-[9px] mt-1.5 italic">Contact your customs broker if unsure</p>
        </div>
      </div>
      <div className="absolute -top-1.5 left-4 w-3 h-3 bg-[#0d1520] border-l border-t border-[#FDB913]/40 rotate-45"></div>
    </div>
  );
}

// ── Request Code options (all 7 from real CBP Form 4811 12/25) ─────────────
const REQUEST_CODES = [
  { code: '10', label: 'Code 10', desc: 'Bills, Refunds and Notices of Liquidation' },
  { code: '20', label: 'Code 20', desc: 'Refunds and Drawbacks Only' },
  { code: '23', label: 'Code 23', desc: 'Bills and Refunds' },
  { code: '24', label: 'Code 24', desc: 'Notices of Liquidation Only' },
  { code: '30', label: 'Code 30', desc: 'Bills Only' },
  { code: '34', label: 'Code 34', desc: 'Refunds and Notices' },
  { code: '40', label: 'Code 40', desc: 'Bills and Notices' },
] as const;

type RequestCode = typeof REQUEST_CODES[number]['code'];

// ── Constants ──────────────────────────────────────────────────────────────
const FORM_SUBMIT_URL = 'https://readdy.ai/api/form/d6sh9nr4rjv84lqbqdc0';
const AGENT_LAST = 'Thabit';
const AGENT_FIRST = 'Bruce';
const AGENT_MI = 'A';
const AGENT_NUMBER = '***-**-****';
const AGENT_ADDRESS = '1037 NE 65th St #80300, Seattle, WA 98115';

interface Props {
  clientId: string;
  clientName: string;
  clientEmail: string;
  company?: string | null;
  alreadySubmitted?: boolean;
  onSubmitted?: (submittedAt: string) => void;
  importerName?: string | null;
  importerAddress?: string | null;
  importerPhone?: string | null;
}

export default function Form4811({
  clientId, clientName, clientEmail, company,
  alreadySubmitted = false, onSubmitted,
  importerName, importerAddress, importerPhone,
}: Props) {
  const [expanded, setExpanded] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(alreadySubmitted);
  const [error, setError] = useState('');
  const [pdfData, setPdfData] = useState<Form4811PDFData | null>(null);
  const [pdfGenerating, setPdfGenerating] = useState(false);
  const [pdfError, setPdfError] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [showImporterTooltip, setShowImporterTooltip] = useState(false);

  // ── Exact CBP Form 4811 (12/25) fields ────────────────────────────────────
  // Section 1: Request Code (select only one)
  const [requestCode, setRequestCode] = useState<RequestCode>('10');

  // Section 2: Agent Information (pre-filled — read-only for client)
  // AGENT_LAST, AGENT_FIRST, AGENT_MI, AGENT_NUMBER, AGENT_ADDRESS are constants above

  // Section 3: My (Organization's) Importer Number — only field on real CBP Form 4811
  const [importerNumber, setImporterNumber] = useState('');

  // Section 4: Agent's Address (where CBP sends notices — pre-filled)
  // This is AGENT_ADDRESS — read-only

  // Section 5: Signature, Date, Title
  const [sigName, setSigName] = useState('');
  const [sigTitle, setSigTitle] = useState('');
  const [sigDate, setSigDate] = useState(new Date().toISOString().split('T')[0]);
  const [sigAgreed, setSigAgreed] = useState(false);

  const buildCurrentPdfData = (): Form4811PDFData => ({
    importerName: importerName || company || clientName || '',
    importerNumber,
    requestCode,
    agentLastName: AGENT_LAST,
    agentFirstName: AGENT_FIRST,
    agentMiddleInitial: AGENT_MI,
    agentNumber: AGENT_NUMBER,
    agentAddress: AGENT_ADDRESS,
    effectiveDate: sigDate,
    electronicSignature: sigName || clientName,
    signerTitle: sigTitle,
    clientName,
    clientEmail,
    submittedAt: new Date().toISOString(),
    // Legacy fields kept for PDF compat
    importerEin: '',
    importerPhone: importerPhone || '',
    importerCountry: 'United States',
    importerAddress: importerAddress || '',
    importerCity: '',
    importerState: '',
    importerZip: '',
    dateOfLiquidation: '',
    requestType: requestCode === '10' ? '10' : '20',
    capacity: 'principal',
  });

  // Load saved data when already submitted
  useEffect(() => {
    if (!alreadySubmitted || !clientId) return;
    const loadSavedData = async () => {
      try {
        const { data } = await supabase
          .from('clients')
          .select('name, email, company, importer_of_record_name, form_4811_submitted_at, form_4811_data')
          .eq('id', clientId)
          .maybeSingle();

        const row = (data || {}) as Record<string, unknown>;
        const submittedAt = (row.form_4811_submitted_at as string) || new Date().toISOString();
        const saved = row.form_4811_data as Record<string, string> | null;

        setPdfData({
          importerName: (row.importer_of_record_name as string) || importerName || company || clientName || '',
          importerNumber: saved?.importer_number || '',
          requestCode: (saved?.request_code as RequestCode) || '10',
          agentLastName: AGENT_LAST,
          agentFirstName: AGENT_FIRST,
          agentMiddleInitial: AGENT_MI,
          agentNumber: AGENT_NUMBER,
          agentAddress: AGENT_ADDRESS,
          effectiveDate: saved?.effective_date || submittedAt,
          electronicSignature: saved?.electronic_signature || clientName,
          signerTitle: saved?.capacity_title || '',
          clientName,
          clientEmail,
          submittedAt,
          importerEin: '',
          importerPhone: importerPhone || '',
          importerCountry: 'United States',
          importerAddress: importerAddress || '',
          importerCity: '',
          importerState: '',
          importerZip: '',
          dateOfLiquidation: '',
          requestType: '10',
          capacity: 'principal',
        });
      } catch {
        setPdfData({
          importerName: importerName || company || clientName || '',
          importerNumber: '',
          requestCode: '10',
          agentLastName: AGENT_LAST,
          agentFirstName: AGENT_FIRST,
          agentMiddleInitial: AGENT_MI,
          agentNumber: AGENT_NUMBER,
          agentAddress: AGENT_ADDRESS,
          effectiveDate: new Date().toISOString(),
          electronicSignature: clientName,
          signerTitle: '',
          clientName,
          clientEmail,
          submittedAt: new Date().toISOString(),
          importerEin: '',
          importerPhone: importerPhone || '',
          importerCountry: 'United States',
          importerAddress: importerAddress || '',
          importerCity: '',
          importerState: '',
          importerZip: '',
          dateOfLiquidation: '',
          requestType: '10',
          capacity: 'principal',
        });
      }
    };
    loadSavedData();
  }, [alreadySubmitted, clientId, clientName, clientEmail, company, importerName, importerAddress, importerPhone]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (submitting) return;
    if (!sigAgreed) { setError('You must agree to the electronic signature certification before submitting.'); return; }
    if (!sigName.trim()) { setError('Please type your full legal name as your electronic signature.'); return; }
    if (!importerNumber.trim()) { setError('Your (Organization\'s) Importer Number is required.'); return; }
    if (!sigTitle.trim()) { setError('Please select or enter your capacity/title (Principal, Member of Firm, or corporate title).'); return; }

    const body = new URLSearchParams();
    body.append('form_title', 'CBP Form 4811 (12/25) - Special Address Notification');
    body.append('client_name', clientName);
    body.append('email', clientEmail);
    body.append('company', company || '');
    body.append('submission_date', new Date().toISOString());
    body.append('request_code', requestCode);
    body.append('request_code_description', REQUEST_CODES.find(c => c.code === requestCode)?.desc || '');
    body.append('agent_last_name', AGENT_LAST);
    body.append('agent_first_name', AGENT_FIRST);
    body.append('agent_middle_initial', AGENT_MI);
    body.append('agent_number', AGENT_NUMBER);
    body.append('agent_address', AGENT_ADDRESS);
    body.append('importer_number', importerNumber);
    body.append('electronic_signature', sigName);
    body.append('capacity_title', sigTitle);
    body.append('effective_date', sigDate);

    setSubmitting(true);
    setError('');

    try {
      const res = await fetch(FORM_SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (!res.ok) throw new Error('Submission failed. Please try again.');

      const submittedAt = new Date().toISOString();
      const form4811DataToSave = {
        importer_number: importerNumber,
        request_code: requestCode,
        effective_date: sigDate,
        electronic_signature: sigName,
        capacity_title: sigTitle,
        agent_last: AGENT_LAST,
        agent_first: AGENT_FIRST,
        agent_mi: AGENT_MI,
        agent_number: AGENT_NUMBER,
        agent_address: AGENT_ADDRESS,
      };

      await supabase.from('clients')
        .update({ form_4811_submitted_at: submittedAt, form_4811_data: form4811DataToSave })
        .eq('id', clientId);

      const captured: Form4811PDFData = {
        ...buildCurrentPdfData(),
        electronicSignature: sigName,
        submittedAt,
      };
      setPdfData(captured);
      setSubmitted(true);
      if (onSubmitted) onSubmitted(submittedAt);

      // Notify client
      try {
        await supabase.functions.invoke('send-client-notification', {
          body: { eventType: 'form_4811_submitted', clientName, clientEmail, timestamp: submittedAt, portalUrl: 'https://tariffrefund.attorney/portal' },
        });
      } catch { /* non-fatal */ }

      // Notify attorney (admin)
      try {
        await supabase.functions.invoke('send-admin-notification', {
          body: {
            eventType: 'form_4811_submitted',
            clientName,
            clientEmail,
            company: company || '',
            submittedAt,
            timestamp: submittedAt,
          },
        });
      } catch { /* non-fatal */ }

      // Insert portal notification for client
      try {
        await supabase.from('client_notifications').insert({
          client_id: clientId,
          title: 'CBP Form 4811 Submitted',
          message: 'Your Special Address Notification (CBP Form 4811) has been received. Your attorney will review and process it shortly.',
          notification_type: 'form_submitted',
          is_read: false,
        });
      } catch { /* non-fatal */ }
    } catch (err: unknown) {
      setError((err as Error).message || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!pdfData) return;
    setPdfGenerating(true);
    setPdfError('');
    try {
      const bytes = await generateForm4811PDF(pdfData);
      const safeName = clientName.replace(/[^a-z0-9]/gi, '_');
      downloadPDF(bytes, `CBP_Form_4811_${safeName}_${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (err) {
      setPdfError('PDF generation failed. Please try again.');
      console.error('PDF generation error:', err);
    } finally {
      setPdfGenerating(false);
    }
  };

  // ── Submitted state ────────────────────────────────────────────────────────
  if (submitted) {
    return (
      <div className="bg-white/5 backdrop-blur-md border border-green-500/30 rounded-2xl p-8">
        <div className="text-center py-6">
          <div className="w-16 h-16 flex items-center justify-center bg-green-500/20 rounded-full border-2 border-green-500/40 mx-auto mb-4">
            <i className="ri-checkbox-circle-fill text-green-400 text-4xl"></i>
          </div>
          <h3 className="text-2xl font-black text-white mb-2">Form 4811 Submitted</h3>
          <p className="text-white/60 text-sm leading-relaxed max-w-md mx-auto">
            Your CBP Special Address Notification has been received. Your attorney will now be designated
            to receive electronic ACH refunds on your behalf from U.S. Customs and Border Protection.
          </p>
          <p className="text-[#FDB913] text-xs mt-4 font-bold">DHS/CBP Form 4811 (12/25) — Special Address Notification</p>
          {pdfData ? (
            <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
              <button
                onClick={handleDownloadPDF}
                disabled={pdfGenerating}
                className="flex items-center gap-2 px-6 py-3 bg-[#FDB913] hover:bg-[#e6a800] text-black font-black text-sm rounded-xl transition-all disabled:opacity-50 whitespace-nowrap cursor-pointer"
              >
                {pdfGenerating
                  ? <><i className="ri-loader-4-line animate-spin"></i>Generating PDF...</>
                  : <><i className="ri-file-pdf-line text-lg"></i>Download Signed PDF</>}
              </button>
              <p className="text-white/40 text-xs"><i className="ri-information-line mr-1"></i>Save a copy for your records</p>
            </div>
          ) : (
            <div className="mt-6 flex items-center justify-center gap-2">
              <i className="ri-loader-4-line animate-spin text-white/30 text-sm"></i>
              <p className="text-white/30 text-xs">Loading PDF data...</p>
            </div>
          )}
          {pdfError && (
            <div className="mt-3 flex items-center justify-center gap-2 text-red-400 text-xs">
              <i className="ri-error-warning-line"></i>{pdfError}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── Form ───────────────────────────────────────────────────────────────────
  return (
    <>
      {showPreview && (
        <PDFPreviewModal
          onClose={() => setShowPreview(false)}
          onGenerate={() => generateForm4811PDF(buildCurrentPdfData())}
          formTitle="CBP Form 4811"
        />
      )}

      <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl overflow-hidden">
        {/* Header toggle */}
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full flex items-center justify-between p-6 hover:bg-white/5 transition-colors cursor-pointer text-left"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 flex items-center justify-center bg-[#FDB913]/20 rounded-xl border border-[#FDB913]/40 flex-shrink-0">
              <i className="ri-bank-card-line text-[#FDB913] text-2xl"></i>
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="text-lg font-black text-white">CBP Form 4811</h3>
                <span className="bg-[#FDB913]/20 border border-[#FDB913]/40 text-[#FDB913] text-xs font-black px-2 py-0.5 rounded-full whitespace-nowrap">
                  Action Required
                </span>
                <span className="bg-white/10 text-white/50 text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap">
                  Rev. 12/2025
                </span>
              </div>
              <p className="text-white/50 text-xs mt-0.5">Special Address Notification — ACH Electronic Refund Designation</p>
            </div>
          </div>
          <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
            <i className={`${expanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-white/60 text-xl`}></i>
          </div>
        </button>

        {expanded && (
          <div className="border-t border-white/10 p-6">
            {/* Info note */}
            <div className="bg-gradient-to-r from-[#FDB913]/10 to-[#FDB913]/5 border border-[#FDB913]/30 rounded-xl p-4 mb-6">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 flex items-center justify-center bg-[#FDB913]/20 rounded-lg border border-[#FDB913]/40 flex-shrink-0">
                  <i className="ri-information-line text-[#FDB913] text-lg"></i>
                </div>
                <div>
                  <p className="text-[#FDB913] font-black text-sm mb-1">CBP Form 4811 — Special Address Notification (Rev. 12/2025)</p>
                  <p className="text-white/60 text-xs leading-relaxed">
                    This is the <strong className="text-white">current required version</strong> (effective Feb 6, 2026 — older versions no longer accepted).
                    It designates your Attorney to receive electronic ACH refunds from CBP on your behalf.
                    Agent fields are pre-filled. You only need to: <strong className="text-white">(1)</strong> select a request code,
                    <strong className="text-white"> (2)</strong> enter your Importer Number, and <strong className="text-white">(3)</strong> sign.
                  </p>
                </div>
              </div>
            </div>

            <form
              data-readdy-form
              id={`cbp-form-4811-${clientId}`}
              onSubmit={handleSubmit}
            >
              {/* ── OFFICIAL FORM REPLICA ── */}
              <div className="border-2 border-[#1a2a3a] rounded-xl overflow-hidden font-sans bg-white">

                {/* ── FORM HEADER (matches real CBP form header) ── */}
                <div className="bg-white border-b-2 border-gray-400 px-5 py-3">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      {/* DHS seal placeholder + title block */}
                      <div className="flex items-start gap-3">
                        <div className="w-12 h-12 flex-shrink-0 flex items-center justify-center border-2 border-gray-400 rounded-full bg-gray-100">
                          <i className="ri-shield-star-line text-gray-500 text-xl"></i>
                        </div>
                        <div>
                          <p className="text-gray-700 text-[10px] font-bold uppercase tracking-wide">Department of Homeland Security</p>
                          <p className="text-gray-900 text-sm font-black">U.S. Customs and Border Protection</p>
                          <p className="text-gray-900 font-black text-lg mt-1 tracking-wide uppercase">Special Address Notification</p>
                        </div>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0 border border-gray-400 p-2 rounded">
                      <p className="text-gray-900 font-black text-sm">CBP Form 4811</p>
                      <p className="text-gray-500 text-[9px] mt-0.5">OMB No. 1651-0076</p>
                      <p className="text-gray-500 text-[9px]">Revised 12/2025</p>
                      <p className="text-gray-500 text-[9px] mt-1 border-t border-gray-300 pt-1">Expires 12/2028</p>
                    </div>
                  </div>
                  <div className="mt-2 bg-gray-100 border border-gray-300 rounded p-2">
                    <p className="text-gray-600 text-[9px] leading-relaxed">
                      <strong>PAPERWORK REDUCTION ACT NOTICE:</strong> An agency may not conduct or sponsor, and a person is not required to respond to, a collection of information unless it displays a valid OMB control number. The estimated average burden associated with this collection of information is 5 minutes per respondent or recordkeeper depending on individual circumstances. Comments concerning the accuracy of this burden estimate and suggestions for reducing this burden should be directed to U.S. Customs and Border Protection, Office of Regulations and Rulings, 90 K Street, NE, Washington DC 20229.
                    </p>
                  </div>
                </div>

                {/* ── SECTION 1: REQUEST CODE ── */}
                <div className="border-b-2 border-gray-400">
                  <div className="bg-gray-200 px-4 py-1.5 border-b border-gray-400">
                    <p className="text-gray-900 text-[10px] font-black uppercase tracking-wider">
                      Select Request Code — Check Only One Box
                    </p>
                  </div>
                  <div className="px-4 py-3 bg-white">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {REQUEST_CODES.map((opt) => (
                        <button
                          key={opt.code}
                          type="button"
                          onClick={() => setRequestCode(opt.code)}
                          className={`flex items-start gap-3 p-2.5 border text-left transition-all cursor-pointer ${
                            requestCode === opt.code
                              ? 'border-gray-900 bg-gray-50'
                              : 'border-gray-300 hover:border-gray-500 bg-white'
                          }`}
                        >
                          {/* Checkbox style (matches real form) */}
                          <div className={`w-4 h-4 flex-shrink-0 border-2 flex items-center justify-center mt-0.5 ${
                            requestCode === opt.code ? 'border-gray-900 bg-gray-900' : 'border-gray-500 bg-white'
                          }`}>
                            {requestCode === opt.code && (
                              <i className="ri-check-line text-white text-[9px]"></i>
                            )}
                          </div>
                          <div>
                            <p className="text-gray-900 font-black text-xs">{opt.label}</p>
                            <p className="text-gray-600 text-[10px] leading-snug">{opt.desc}</p>
                          </div>
                        </button>
                      ))}
                    </div>
                    <p className="text-gray-500 text-[9px] mt-2 italic">
                      * Code 10 is most commonly used for tariff refund cases (Bills, Refunds and Notices of Liquidation)
                    </p>
                  </div>
                </div>

                {/* ── SECTION 2: AGENT INFORMATION (Pre-filled) ── */}
                <div className="border-b-2 border-gray-400">
                  <div className="bg-gray-200 px-4 py-1.5 border-b border-gray-400">
                    <p className="text-gray-900 text-[10px] font-black uppercase tracking-wider">
                      Agent&apos;s Name and Number — Notify Party
                    </p>
                  </div>
                  <div className="bg-white">
                    {/* Agent Name row */}
                    <div className="grid grid-cols-12 border-b border-gray-300">
                      <div className="col-span-4 border-r border-gray-300 p-0">
                        <div className="bg-gray-100 px-2 py-1 border-b border-gray-300">
                          <p className="text-gray-600 text-[9px] font-bold uppercase">Last Name</p>
                        </div>
                        <div className="px-3 py-2 bg-[#fffbf0]">
                          <p className="text-gray-900 font-black text-sm">{AGENT_LAST}</p>
                        </div>
                      </div>
                      <div className="col-span-4 border-r border-gray-300 p-0">
                        <div className="bg-gray-100 px-2 py-1 border-b border-gray-300">
                          <p className="text-gray-600 text-[9px] font-bold uppercase">First Name</p>
                        </div>
                        <div className="px-3 py-2 bg-[#fffbf0]">
                          <p className="text-gray-900 font-black text-sm">{AGENT_FIRST}</p>
                        </div>
                      </div>
                      <div className="col-span-4 p-0">
                        <div className="bg-gray-100 px-2 py-1 border-b border-gray-300">
                          <p className="text-gray-600 text-[9px] font-bold uppercase">Middle Initial</p>
                        </div>
                        <div className="px-3 py-2 bg-[#fffbf0]">
                          <p className="text-gray-900 font-black text-sm">{AGENT_MI}</p>
                        </div>
                      </div>
                    </div>
                    {/* Agent Number row */}
                    <div className="grid grid-cols-12 border-b border-gray-300">
                      <div className="col-span-5 border-r border-gray-300 p-0">
                        <div className="bg-gray-100 px-2 py-1 border-b border-gray-300">
                          <p className="text-gray-600 text-[9px] font-bold uppercase">Agent&apos;s Importer Number (with hyphens)</p>
                        </div>
                        <div className="px-3 py-2 bg-[#fffbf0]">
                          <p className="text-gray-900 font-black text-sm tracking-widest">{AGENT_NUMBER}</p>
                          <p className="text-gray-400 text-[9px] mt-0.5">Will be completed by attorney</p>
                        </div>
                      </div>
                      <div className="col-span-7 p-0">
                        <div className="bg-gray-100 px-2 py-1 border-b border-gray-300">
                          <p className="text-gray-600 text-[9px] font-bold uppercase">Agent&apos;s Address (Street, City, State, ZIP)</p>
                        </div>
                        <div className="px-3 py-2 bg-[#fffbf0]">
                          <p className="text-gray-900 text-xs font-bold">{AGENT_ADDRESS}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* ── SECTION 3: MY (ORGANIZATION'S) IMPORTER NUMBER ── */}
                <div className="border-b-2 border-gray-400">
                  <div className="bg-gray-200 px-4 py-1.5 border-b border-gray-400 flex items-center justify-between">
                    <p className="text-gray-900 text-[10px] font-black uppercase tracking-wider">
                      My (Organization&apos;s) Importer Number (Show hyphens):
                    </p>
                    <div className="relative flex-shrink-0 ml-2">
                      <button
                        type="button"
                        onMouseEnter={() => setShowImporterTooltip(true)}
                        onMouseLeave={() => setShowImporterTooltip(false)}
                        onClick={() => setShowImporterTooltip((v) => !v)}
                        className="w-4 h-4 flex items-center justify-center bg-[#1a3a5c] rounded-full cursor-pointer"
                        aria-label="Where to find Importer Number"
                      >
                        <i className="ri-question-line text-white text-[9px]"></i>
                      </button>
                      <ImporterNumberTooltip visible={showImporterTooltip} />
                    </div>
                  </div>
                  <div className="bg-white">
                    {/* Single field — exactly as on real CBP Form 4811 */}
                    <input
                      type="text"
                      required
                      maxLength={50}
                      value={importerNumber}
                      onChange={(e) => setImporterNumber(e.target.value)}
                      className="w-full px-4 py-3 bg-white text-gray-900 text-sm focus:outline-none focus:bg-[#fffbf0] transition-colors"
                      placeholder="e.g. 12-345678900 (EIN with hyphens, SSN, or CBP-assigned number)"
                    />
                    <div className="px-4 pb-2">
                      <p className="text-gray-400 text-[9px] italic">
                        Must have a CBP Form 5106 on file. Show hyphens (e.g. EIN: 12-3456789, SSN: 123-45-6789).
                      </p>
                    </div>
                  </div>
                </div>

                {/* ── SECTION 4: SIGNATURE, TITLE AND DATE ── */}
                <div className="border-b border-gray-300">
                  <div className="bg-gray-200 px-4 py-1.5 border-b border-gray-400">
                    <p className="text-gray-900 text-[10px] font-black uppercase tracking-wider">
                      Signature, Title and Date
                    </p>
                  </div>
                  <div className="bg-white px-4 py-3 space-y-3">
                    {/* ESIGN disclosure */}
                    <div className="bg-gray-50 border border-gray-300 p-3">
                      <p className="text-gray-600 text-[9px] leading-relaxed">
                        <strong className="text-gray-800">CERTIFICATION:</strong> I hereby certify that the information provided is true and accurate.
                        I authorize Attorney Bruce A. Thabit / Serenity Law PLLC to receive electronic ACH refunds from U.S. Customs and Border Protection on my behalf.
                        By typing my name below, I acknowledge this constitutes my legally binding electronic signature under the ESIGN Act (15 U.S.C. § 7001).
                        False statements may subject me to criminal and civil penalties (18 U.S.C. § 1001).
                      </p>
                    </div>

                    {/* Signature + Date row */}
                    <div className="grid grid-cols-12 gap-0 border border-gray-300">
                      <div className="col-span-8 border-r border-gray-300">
                        <div className="bg-gray-100 px-3 py-1 border-b border-gray-300">
                          <p className="text-gray-600 text-[9px] font-bold uppercase">
                            Signature (Type Full Legal Name) <span className="text-red-600">*</span>
                          </p>
                        </div>
                        <input
                          type="text"
                          required
                          maxLength={200}
                          value={sigName}
                          onChange={(e) => setSigName(e.target.value)}
                          className="w-full px-3 py-3 bg-white text-gray-900 text-base focus:outline-none focus:bg-[#fffbf0] transition-colors"
                          placeholder="Type your full legal name..."
                          style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic' }}
                        />
                      </div>
                      <div className="col-span-4">
                        <div className="bg-gray-100 px-3 py-1 border-b border-gray-300">
                          <p className="text-gray-600 text-[9px] font-bold uppercase">Date <span className="text-red-600">*</span></p>
                        </div>
                        <input
                          type="date"
                          required
                          value={sigDate}
                          onChange={(e) => setSigDate(e.target.value)}
                          className="w-full px-3 py-3 bg-white text-gray-900 text-sm focus:outline-none focus:bg-[#fffbf0] transition-colors"
                        />
                      </div>
                    </div>

                    {/* ── CAPACITY — exact CBP Form 4811 layout ── */}
                    <div className="border border-gray-300">
                      <div className="bg-gray-100 px-3 py-1 border-b border-gray-300">
                        <p className="text-gray-600 text-[9px] font-bold uppercase">
                          Capacity — Check One <span className="text-red-600">*</span>
                        </p>
                      </div>
                      <div className="px-3 py-2.5 space-y-2">
                        {/* Principal */}
                        <button
                          type="button"
                          onClick={() => setSigTitle('Principal')}
                          className="flex items-center gap-2.5 w-full text-left cursor-pointer group"
                        >
                          <div className={`w-4 h-4 flex-shrink-0 border-2 flex items-center justify-center transition-all ${
                            sigTitle === 'Principal' ? 'border-gray-900 bg-gray-900' : 'border-gray-500 bg-white group-hover:border-gray-700'
                          }`}>
                            {sigTitle === 'Principal' && <i className="ri-check-line text-white text-[9px]"></i>}
                          </div>
                          <span className="text-gray-800 text-xs font-bold">Principal</span>
                        </button>

                        {/* Member of Firm */}
                        <button
                          type="button"
                          onClick={() => setSigTitle('Member of Firm')}
                          className="flex items-center gap-2.5 w-full text-left cursor-pointer group"
                        >
                          <div className={`w-4 h-4 flex-shrink-0 border-2 flex items-center justify-center transition-all ${
                            sigTitle === 'Member of Firm' ? 'border-gray-900 bg-gray-900' : 'border-gray-500 bg-white group-hover:border-gray-700'
                          }`}>
                            {sigTitle === 'Member of Firm' && <i className="ri-check-line text-white text-[9px]"></i>}
                          </div>
                          <span className="text-gray-800 text-xs font-bold">Member of Firm</span>
                        </button>

                        {/* _____________ of the Corp. (Title) */}
                        <div className="flex items-center gap-2.5">
                          <button
                            type="button"
                            onClick={() => {
                              if (sigTitle !== 'Principal' && sigTitle !== 'Member of Firm') return;
                              setSigTitle('');
                            }}
                            className="flex-shrink-0 cursor-pointer group"
                          >
                            <div className={`w-4 h-4 border-2 flex items-center justify-center transition-all ${
                              sigTitle !== 'Principal' && sigTitle !== 'Member of Firm'
                                ? 'border-gray-900 bg-gray-900'
                                : 'border-gray-500 bg-white group-hover:border-gray-700'
                            }`}>
                              {sigTitle !== 'Principal' && sigTitle !== 'Member of Firm' && (
                                <i className="ri-check-line text-white text-[9px]"></i>
                              )}
                            </div>
                          </button>
                          <div className="flex-1 flex items-center gap-1.5">
                            <input
                              type="text"
                              maxLength={80}
                              value={sigTitle !== 'Principal' && sigTitle !== 'Member of Firm' ? sigTitle : ''}
                              onFocus={() => {
                                if (sigTitle === 'Principal' || sigTitle === 'Member of Firm') setSigTitle('');
                              }}
                              onChange={(e) => setSigTitle(e.target.value)}
                              className="flex-1 border-b border-gray-400 bg-transparent text-gray-900 text-xs focus:outline-none focus:border-gray-700 py-0.5 min-w-0"
                              placeholder="Title (e.g. President, CEO, Director)"
                            />
                            <span className="text-gray-700 text-xs font-bold whitespace-nowrap">of the Corp.</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Certification checkbox */}
                    <button
                      type="button"
                      onClick={() => setSigAgreed(!sigAgreed)}
                      className={`w-full flex items-start gap-3 p-3 border-2 text-left transition-all cursor-pointer ${
                        sigAgreed ? 'bg-green-50 border-green-500' : 'bg-gray-50 border-gray-300 hover:border-gray-500'
                      }`}
                    >
                      <div className={`w-5 h-5 flex-shrink-0 border-2 flex items-center justify-center mt-0.5 transition-all ${
                        sigAgreed ? 'bg-green-500 border-green-500' : 'border-gray-400 bg-white'
                      }`}>
                        {sigAgreed && <i className="ri-check-line text-white text-xs"></i>}
                      </div>
                      <p className="text-gray-600 text-[10px] leading-relaxed">
                        I certify the above information is true and accurate. I authorize Attorney Bruce A. Thabit / Serenity Law PLLC
                        to receive electronic ACH refund payments from U.S. Customs and Border Protection on my behalf.
                        This is my legally binding electronic signature under the ESIGN Act (15 U.S.C. § 7001).
                      </p>
                    </button>
                  </div>
                </div>

                {/* Form Footer */}
                <div className="bg-gray-100 px-4 py-2 border-t border-gray-400">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <p className="text-gray-500 text-[9px]">CBP Form 4811 (12/25) — OMB No. 1651-0076 — Paperwork Reduction Act Notice</p>
                    <p className="text-gray-500 text-[9px]">ESIGN Act Compliant Electronic Record</p>
                  </div>
                </div>
              </div>
              {/* ── END FORM REPLICA ── */}

              {error && (
                <div className="mt-4 flex items-center gap-2 bg-red-500/10 border border-red-500/20 rounded-lg px-4 py-3">
                  <i className="ri-error-warning-line text-red-400"></i>
                  <p className="text-red-400 text-sm">{error}</p>
                </div>
              )}

              {/* Action buttons */}
              <div className="mt-5 flex flex-col sm:flex-row gap-3">
                <button
                  type="button"
                  onClick={() => setShowPreview(true)}
                  className="flex-1 py-3.5 bg-white/10 hover:bg-white/20 border border-white/20 text-white font-black text-sm rounded-xl transition-all whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
                >
                  <i className="ri-eye-line text-lg"></i>Preview PDF
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 py-3.5 bg-[#FDB913] hover:bg-[#e6a800] text-black font-black text-sm rounded-xl transition-all disabled:opacity-50 whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
                >
                  {submitting ? (
                    <><i className="ri-loader-4-line animate-spin"></i>Submitting...</>
                  ) : (
                    <><i className="ri-send-plane-fill"></i>Submit CBP Form 4811</>
                  )}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </>
  );
}
